import { useEffect, useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import { AutoCompleteSelectEvent } from 'primereact/autocomplete';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { PartnershipsActions } from '@/app/slices/partnerships';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { queryClient } from '@/services';
import {
  partnershipsOptions,
  upsertPartnershipV2,
} from '@/services/partnerships';
import { UPDATE_TEMP_EMBRYO_OWNERS } from '@/types.gql';
import { determineBillingSplitType } from '@/utils';

import { AccountState, PercentState } from '../../../../types';
import {
  BillingSplitLabel,
  BillingSplitType,
  NULL_ACCOUNT_STATE,
  ONE_HUNDRED_PERCENT_STATE,
  PartnershipBillingType,
  PartnershipTypeValue,
  TWENTY_FIVE_PERCENT_STATE,
} from '../../constants';

export const usePartnershipForm = (type: number, viewOnly: boolean = false) => {
  const dispatch = useAppDispatch();
  const { partnership } = useAppSelector((state) => state.partnerships);
  const [updateTempEmbryoOwners] = useMutation(UPDATE_TEMP_EMBRYO_OWNERS);
  const currentPartnershipItem = partnership?.[0];
  const { workingDonorSchedule } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const [tempAccounts, setTempAccounts] = useState<AccountState>(
    type === PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING
      ? {
          account1: null,
          account2: null,
          account3: null,
          account4: null,
        }
      : NULL_ACCOUNT_STATE,
  );
  const [tempPercents, setTempPercents] = useState<PercentState>(
    type === PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING
      ? {
          percent1: null,
          percent2: null,
          percent3: null,
          percent4: null,
        }
      : type === PartnershipTypeValue.SPECIAL_BILLING
        ? TWENTY_FIVE_PERCENT_STATE
        : ONE_HUNDRED_PERCENT_STATE,
  );

  const isPartnershipUpdateEnabled = useFeatureFlag('Partnership_Update');

  const lockedSplitType = useMemo(
    () =>
      type === PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING
        ? determineBillingSplitType(currentPartnershipItem)
        : null,
    [currentPartnershipItem, type],
  );

  useEffect(() => {
    if (!isPartnershipUpdateEnabled) return;
    if (!currentPartnershipItem) return;
    const { account1, account2, account3, account4 } = currentPartnershipItem;
    if (!account1 && !account2 && !account3 && !account4) return;
    setTempAccounts((prev) => {
      const next = {
        account1: account1 ?? null,
        account2: account2 ?? null,
        account3: account3 ?? null,
        account4: account4 ?? null,
      };
      if (
        prev.account1?.id === next.account1?.id &&
        prev.account2?.id === next.account2?.id &&
        prev.account3?.id === next.account3?.id &&
        prev.account4?.id === next.account4?.id
      ) {
        return prev;
      }
      return next;
    });
  }, [currentPartnershipItem, isPartnershipUpdateEnabled]);

  const handleAccountSelect = (
    e: AutoCompleteSelectEvent,
    accountNum: number,
  ) => {
    setTempAccounts((prev) => ({
      ...prev,
      [`account${accountNum}`]: e.value,
    }));
  };

  const validatePartnershipSplit = (
    splitType: BillingSplitType,
    accountIds: Set<string>,
    accountCounts: Map<string, number>,
  ): { isValid: boolean; error?: string } => {
    const uniqueAccountCount = accountIds.size;
    const counts = Array.from(accountCounts.values());

    const lockPrefix =
      isPartnershipUpdateEnabled &&
      type === PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING &&
      lockedSplitType
        ? `This partnership is locked to ${BillingSplitLabel[lockedSplitType]} — `
        : '';

    if (type === PartnershipTypeValue.BILL_ACCOUNT) {
      if (tempPercents.percent1 !== 100) {
        return {
          isValid: false,
          error:
            'For bill account, you must have exactly one account with 100%',
        };
      }
      if (uniqueAccountCount !== 1) {
        return {
          isValid: false,
          error: 'For bill account, you must have exactly one account',
        };
      }
      return { isValid: true };
    }
    switch (splitType) {
      case BillingSplitType.FIFTY_FIFTY:
        if (uniqueAccountCount !== 2 || !counts.every((count) => count === 2)) {
          return {
            isValid: false,
            error: `${lockPrefix}For 50-50 split, you must have exactly two accounts with two slots each`,
          };
        }
        break;

      case BillingSplitType.SEVENTY_FIVE_TWENTY_FIVE:
        if (uniqueAccountCount !== 2 || !counts.some((count) => count === 3)) {
          return {
            isValid: false,
            error: `${lockPrefix}For 75-25 split, you must have one account with three slots and one account with one slot`,
          };
        }
        break;

      case BillingSplitType.THIRTY_THREE_EACH:
        if (uniqueAccountCount < 2 || uniqueAccountCount > 3) {
          return {
            isValid: false,
            error: `${lockPrefix}For 33-33-34 split, you must have either two accounts (one appearing twice for 2/3rds, one appearing once for 1/3rd) or three different accounts`,
          };
        }
        if (uniqueAccountCount === 2) {
          const hasAccountWithTwoSlots = counts.some((count) => count === 2);
          const hasAccountWithOneSlot = counts.some((count) => count === 1);
          if (!hasAccountWithTwoSlots || !hasAccountWithOneSlot) {
            return {
              isValid: false,
              error: `${lockPrefix}For 33-33-34 split with two accounts, one account must appear twice (2/3rds) and one account must appear once (1/3rd)`,
            };
          }
        }
        break;

      case BillingSplitType.FIFTY_TWENTY_FIVE_TWENTY_FIVE:
        if (uniqueAccountCount !== 3) {
          return {
            isValid: false,
            error: `${lockPrefix}For 50-25-25 split, you must have exactly three different accounts`,
          };
        }
        if (!counts.includes(2) || counts.filter((c) => c === 1).length !== 2) {
          return {
            isValid: false,
            error: `${lockPrefix}For 50-25-25 split, one account must have two slots and two accounts must have one slot each`,
          };
        }
        break;

      case BillingSplitType.TWENTY_FIVE_EACH:
        if (uniqueAccountCount !== 4) {
          return {
            isValid: false,
            error: `${lockPrefix}For 25-25-25-25 split, you must have four different accounts`,
          };
        }
        break;
    }

    return { isValid: true };
  };

  const collectAccountData = () => {
    const accountIds = new Set<string>();
    const accountCounts = new Map<string, number>();
    const accountPositions = [1, 2, 3, 4];

    accountPositions.forEach((pos) => {
      const account =
        tempAccounts[`account${pos}` as keyof typeof tempAccounts];
      if (account?.id) {
        accountIds.add(account.id);
        accountCounts.set(account.id, (accountCounts.get(account.id) || 0) + 1);
      }
    });

    return { accountIds, accountCounts };
  };

  const validateAndShowError = (
    splitType: BillingSplitType,
    accountIds: Set<string>,
    accountCounts: Map<string, number>,
  ): boolean => {
    const validation = validatePartnershipSplit(
      splitType,
      accountIds,
      accountCounts,
    );

    if (!validation.isValid) {
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Invalid Partnership',
          detail: validation.error,
          life: ToastLife.DEFAULT,
        }),
      );
      return false;
    }

    return true;
  };

  const calculateAccountPositions = (accountCounts: Map<string, number>) => {
    const majorityAccountId = Array.from(accountCounts.entries()).find(
      ([, count]) => count === 2,
    )?.[0];

    return [1, 2, 3, 4].reduce(
      (acc, pos) => {
        const account =
          tempAccounts[`account${pos}` as keyof typeof tempAccounts];
        if (account?.id === majorityAccountId) {
          acc.majority.push(pos);
        } else if (account?.id) {
          acc.minority.push(pos);
        }
        return acc;
      },
      { majority: [] as number[], minority: [] as number[] },
    );
  };

  const applyPercentages = (
    updatedPartnership: any,
    splitType: BillingSplitType,
    positions: { majority: number[]; minority: number[] },
  ) => {
    if (type === PartnershipTypeValue.BILL_ACCOUNT) {
      return {
        ...updatedPartnership,
        percent1: 100,
        percent2: 0,
        percent3: 0,
        percent4: 0,
        account2: null,
        account3: null,
        account4: null,
      };
    }

    switch (splitType) {
      case BillingSplitType.FIFTY_FIFTY:
      case BillingSplitType.SEVENTY_FIVE_TWENTY_FIVE:
      case BillingSplitType.TWENTY_FIVE_EACH:
        return {
          ...updatedPartnership,
          percent1: 25,
          percent2: 25,
          percent3: 25,
          percent4: 25,
        };

      case BillingSplitType.THIRTY_THREE_EACH:
        return {
          ...updatedPartnership,
          percent1: 33,
          percent2: 33,
          percent3: 34,
          percent4: null,
        };

      case BillingSplitType.FIFTY_TWENTY_FIVE_TWENTY_FIVE:
        if (positions.majority.length < 2 || positions.minority.length < 2) {
          return updatedPartnership;
        }
        return {
          ...updatedPartnership,
          [`percent${positions.majority[0]}`]: 25,
          [`percent${positions.majority[1]}`]: 25,
          [`percent${positions.minority[0]}`]: 25,
          [`percent${positions.minority[1]}`]: 25,
        };

      default:
        return updatedPartnership;
    }
  };

  const shouldSaveToDatabase = (splitType: BillingSplitType): boolean => {
    if (splitType === BillingSplitType.ONE_HUNDRED) {
      return !!tempAccounts.account1;
    }

    if (splitType === BillingSplitType.THIRTY_THREE_EACH) {
      return !!(
        tempAccounts.account1 &&
        tempAccounts.account2 &&
        tempAccounts.account3
      );
    }

    return !!(
      tempAccounts.account1 &&
      tempAccounts.account2 &&
      tempAccounts.account3 &&
      tempAccounts.account4
    );
  };

  const savePartnershipToDatabase = async (updatedPartnership: any) => {
    const {
      account1,
      account2,
      account3,
      account4,
      donor,
      donorSchedule,
      ...partnershipInput
    } = updatedPartnership;

    await upsertPartnershipV2(partnershipInput);

    await updateTempEmbryoOwners({
      variables: {
        donorScheduleId: workingDonorSchedule?.id,
      },
    });

    if (workingDonorSchedule?.donorId && workingDonorSchedule?.id) {
      if (isPartnershipUpdateEnabled) {
        await queryClient.invalidateQueries({ queryKey: ['partnerships'] });
      }
      const refreshedData = await queryClient.fetchQuery(
        partnershipsOptions({
          donorId: workingDonorSchedule.donorId,
          donorScheduleId: workingDonorSchedule.id,
        }),
      );
      const partnership = refreshedData?.[0];

      if (partnership) {
        dispatch({
          type: PartnershipsActions.UPDATE_PARTNERSHIP,
          payload: [
            {
              ...partnership,
              account1: tempAccounts.account1,
              account2: tempAccounts.account2,
              account3: tempAccounts.account3,
              account4: tempAccounts.account4,
              donor: workingDonorSchedule?.donor,
              donorId: workingDonorSchedule?.donorId,
              partnershipId: partnership.partnershipId,
            },
          ],
        });
      }
    }
  };

  const derivePartnershipBillingType = (): number | null => {
    if (type === PartnershipTypeValue.DONOR_PARTNERSHIP_BILLING) {
      return PartnershipBillingType.DONOR_PARTNERSHIP;
    }
    if (type === PartnershipTypeValue.SPECIAL_BILLING) {
      return PartnershipBillingType.SPECIAL_PARTNERSHIP;
    }
    return currentPartnershipItem?.partnershipBillingType ?? null;
  };

  const handleSaveAccountPositions = async (
    splitType: BillingSplitType | undefined,
  ) => {
    if (!splitType) return;

    if (!currentPartnershipItem) return;

    if (
      !workingDonorSchedule?.id ||
      workingDonorSchedule.id === '-1' ||
      workingDonorSchedule.id === ''
    ) {
      dispatch(
        addToast({
          severity: 'warn',
          summary: 'Save Donor Schedule First',
          detail:
            'Please save the donor schedule before creating a partnership. A valid donor schedule ID is required.',
          life: ToastLife.LONG,
        }),
      );
      return;
    }

    const { accountIds, accountCounts } = collectAccountData();

    if (!validateAndShowError(splitType, accountIds, accountCounts)) {
      return;
    }

    let updatedPartnership = {
      ...currentPartnershipItem,
      account1: tempAccounts.account1,
      account1Id: tempAccounts.account1?.id,
      account2: tempAccounts.account2,
      account2Id: tempAccounts.account2?.id,
      account3: tempAccounts.account3,
      account3Id: tempAccounts.account3?.id,
      account4: tempAccounts.account4,
      account4Id: tempAccounts.account4?.id,
      donorScheduleId: workingDonorSchedule.id,
      partnershipBillingType: derivePartnershipBillingType(),
    };
    const positions = calculateAccountPositions(accountCounts);
    updatedPartnership = applyPercentages(
      updatedPartnership,
      splitType,
      positions,
    );

    if (shouldSaveToDatabase(splitType)) {
      await savePartnershipToDatabase(updatedPartnership);
    } else {
      dispatch({
        type: PartnershipsActions.UPDATE_PARTNERSHIP,
        payload: [updatedPartnership],
      });
    }

    dispatch(
      addToast({
        severity: 'success',
        summary: 'Partnership Saved',
        life: ToastLife.DEFAULT,
      }),
    );
  };

  return {
    tempAccounts,
    setTempAccounts,
    tempPercents,
    setTempPercents,
    handleAccountSelect,
    handleSaveAccountPositions,
    currentPartnershipItem,
    viewOnly,
    lockedSplitType,
  };
};
